import { isAxiosError } from 'axios'
import { useRef, useState } from 'react'
import { FormProps } from 'antd'
import { useDebounce } from './useDebounce'
import { usePromise } from './usePromise'

const DEBOUNCE_TIMEOUT = 800

export interface InputData {
  value?: string
  isLoading?: boolean
  errorCode?: number
}

export interface InputsState {
  first: InputData
  second: InputData
}

export interface ChangeHandlerOpts<T = void> {
  debounce?: boolean
  force?: boolean
  args?: T
}

type OnFormFieldsChange = Parameters<Required<FormProps>['onValuesChange']>

interface Props<T> {
  setFirstValue: (value: string) => void
  setSecondValue: (value: string) => void
  calculateFirstValue: (value: string, args?: T) => Promise<string> | string
  calculateSecondValue: (value: string, args?: T) => Promise<string> | string
}

const processError = (error: unknown) => {
  if (isAxiosError(error)) {
    return error.response?.status
  }

  return 500
}

export const useSyncNumericInputs = <T>({
  calculateFirstValue,
  calculateSecondValue,
  setFirstValue,
  setSecondValue,
}: Props<T>) => {
  const [inputsState, setInputsState] = useState<{ first: InputData; second: InputData }>({ first: {}, second: {} })
  const lastValueRef = useRef<{ first?: string; second?: string }>({})

  const { send: fetchFirstInput } = usePromise(
    async (value: string, args?: T) => {
      const calculatedValue = await calculateFirstValue(value, args)
      if (lastValueRef.current.second !== value) {
        return
      }

      setInputsState(prev => ({ ...prev, first: { isLoading: false, value: calculatedValue } }))
      setFirstValue(calculatedValue)

      return calculatedValue
    },
    {
      onError: error => {
        setInputsState(prev => ({ ...prev, first: { isLoading: false, value: '', errorCode: processError(error) } }))
        setFirstValue('')
      },
      showErrorNotification: false,
    }
  )

  const { send: fetchSecondInput } = usePromise(
    async (value: string, args?: T) => {
      const calculatedValue = await calculateSecondValue(value, args)
      if (lastValueRef.current.first !== value) {
        return
      }
      setInputsState(prev => ({ ...prev, second: { isLoading: false, value: calculatedValue } }))
      setSecondValue(calculatedValue)
      return calculatedValue
    },
    {
      onError: error => {
        setInputsState(prev => ({ ...prev, second: { isLoading: false, value: '', errorCode: processError(error) } }))
        setSecondValue('')
      },
      showErrorNotification: false,
    }
  )

  const [debounceFetchFirstInput] = useDebounce(fetchFirstInput, DEBOUNCE_TIMEOUT)
  const [debounceFetchSecondInput] = useDebounce(fetchSecondInput, DEBOUNCE_TIMEOUT)

  const handleFirstInputChange = (value: string, opts?: ChangeHandlerOpts<T>) => {
    if (!opts?.force && inputsState.first.value === value) {
      return
    }

    lastValueRef.current.first = value
    setInputsState({ first: { value }, second: { isLoading: true } })
    const fetcher = opts?.debounce ? debounceFetchSecondInput : fetchSecondInput
    fetcher(value, opts?.args)
  }

  const handleSecondInputChange = (value: string, opts?: ChangeHandlerOpts<T>) => {
    if (!opts?.force && inputsState.second.value === value) {
      return
    }

    lastValueRef.current.second = value
    setInputsState({ second: { value }, first: { isLoading: true } })

    const fetcher = opts?.debounce ? debounceFetchFirstInput : fetchFirstInput
    fetcher(value, opts?.args)
  }

  /**
   * To use in antd Form component: for the cases when inputs are controlled by the Form.
   * If inputs are controlled by you directly use only handleFirstInputChange and handleSecondInputChange
   **/
  const handleFormValuesChange = ({
    fields,
    callbackArgs,
    formArgs,
  }: {
    fields: string[]
    callbackArgs?: T
    formArgs: OnFormFieldsChange
  }) => {
    const [changes] = formArgs
    const [firstFieldName, secondFieldName] = fields
    const changedFields = Object.keys(changes)
    const isFirstChanged = changedFields.includes(firstFieldName)
    const isSecondCHanged = changedFields.includes(secondFieldName)

    if (isFirstChanged) {
      handleFirstInputChange(changes[firstFieldName], { args: callbackArgs, debounce: true })
    }

    if (isSecondCHanged) {
      handleSecondInputChange(changes[secondFieldName], { args: callbackArgs, debounce: true })
    }
  }

  return {
    inputsState,
    handleFirstInputChange,
    handleSecondInputChange,
    handleFormValuesChange,
  }
}
